import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

/**
 * Auth Interceptor - Handles HTTP requests with cookie-based authentication
 *
 * Note: Token refresh is handled proactively by AuthService (background timer).
 * This interceptor only adds headers and handles 401 errors by forcing logout.
 * No 401-based refresh - the background timer refreshes before expiry.
 */

/**
 * Check if we should ignore 401 errors for certain endpoints
 * These endpoints handle their own 401 errors and shouldn't trigger forceLogout
 */
function shouldIgnore401(url: string): boolean {
  const authEndpoints = [
    '/login',
    '/register',
    '/refresh',
    '/forgot-password',
    '/reset-password',
    '/profile'  // Don't redirect on profile check - auth service handles this gracefully
  ];
  return authEndpoints.some(endpoint => url.includes(endpoint));
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService);

  // For cookie-based authentication, we don't need to add Authorization headers
  // The HTTP-only cookies are automatically included by the browser
  // Just ensure withCredentials is set and add standard headers
  // Don't set Content-Type for FormData requests (file uploads) - browser will set it with boundary
  const headers: { [key: string]: string } = {};
  if (!(req.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const authReq = req.clone({
    setHeaders: headers,
    withCredentials: true // This ensures cookies are sent with requests
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // On 401, force logout (token expired and wasn't refreshed in time)
      // Don't try to refresh here - background timer handles proactive refresh
      if (error.status === 401 && !shouldIgnore401(authReq.url)) {
        authService.forceLogout();
      }
      return throwError(() => error);
    })
  );
};
